import SponsorshipsSection from '#app/components/sponsorship-section.tsx'
import WatchCTA from '#app/routes/resources+/watch-cta.tsx'
import affiliateLinks from '#app/utils/affiliateLinks.ts'
import { type Route } from './+types/support.ts'

export const meta: Route.MetaFunction = () => {
  const title = `JaseOwns - Support`
  const description =
    'Thanks for showing your support as I share my journey experiences on YouTube and this site.'
  return [
    { title },
    { name: 'description', content: description },
    {
      name: 'keywords',
      content:
        'support,creator,videos,uo,ultima online,self taught,learning,jaseowns,jase,jason',
    },
    {
      property: 'og:image',
      content: 'https://jaseowns.com/images/sup-dawg.png',
    },
    { property: 'og:image:alt', content: 'Meme about being a developer' },
    { property: 'twitter:card', content: 'summary' },
    { property: 'twitter:creator', content: '@jaseowns' },
    { property: 'twitter:site', content: '@jaseowns' },
    { property: 'og:title', content: title },
    { property: 'og:description', content: description },
  ]
}

/** -----------------------------
 *  Support Card Component
 * ----------------------------- */
function SupportOptionCard({
  href,
  label,
  icon,
  stars,
  percentage,
  preferred = false
}: {
  href: string
  label: string
  icon?: string
  stars: string
  percentage: string
  preferred?: boolean
}) {
  return (
    <a
      href={href}
      className={`hover:scale-115 flex flex-col items-center p-4 px-6 rounded-2xl text-white shadow-lg  duration-200 hover:opacity-80 w-full md:w-52 text-center ${preferred ? 'bg-gradient-to-b from-blue-900 dark:from-blue-900 to-cyan-900 dark:to-cyan-900' : 'bg-cyan-600 dark:bg-cyan-800'}`}
    >
      <span className={`text-xl ${preferred ? 'animate-bounce' : ''}`}>{stars}</span>
      <span className={`font-semibold ${preferred ? 'animate-pulse' : ''}`}>{percentage}</span>
      {icon && <img src={icon} alt={label} className={`h-8 my-2 ${preferred ? 'animate-pulse' : ''}`} />}
      <span className={`${preferred ? 'animate-pulse' : ''}`}>{label}</span>
    </a>
  )
}

/** -----------------------------
 *  Affiliate Card Component
 * ----------------------------- */
function AffiliateCard({
  link,
  image,
  caption,
  description,
  jaseNotes,
}: {
  link: string
  image: string
  caption: string
  description: string
  jaseNotes: string
}) {
  return (
    <figure className="max-w-sm mx-4 p-4 rounded-2xl shadow-md bg-white dark:bg-gray-900">
      <a href={link} title={`Go to ${caption} #ad`}>
        <img
          className="h-auto max-w-full rounded-lg m-auto"
          src={image}
          alt={caption}
        />
        <figcaption className="mt-2 text-sm text-center text-gray-500 dark:text-gray-400">
          {caption}
        </figcaption>
        <p className="text-gray-500 dark:text-gray-400 mb-4">{description}</p>
        <p className="font-medium">
          Jase Notes: {jaseNotes} <span className="text-xs">#ad</span>
        </p>
      </a>
    </figure>
  )
}

/** -----------------------------
 *  Other Affiliate Card Component
 * ----------------------------- */
function OtherAffiliateCard({
  href,
  image,
  caption,
  description,
}: {
  href: string
  image: string
  caption: string
  description: string
}) {
  return (
    <figure className="max-w-sm mx-4 p-4 rounded-2xl shadow-md bg-white dark:bg-gray-900">
      <a href={href} title={`Go to ${caption} #ad`}>
        <p className="mb-6">{description}</p>
        <img
          className="h-auto max-w-full rounded-lg m-auto"
          src={image}
          alt={caption}
        />
        <figcaption className="mt-2 text-sm text-center text-gray-500 dark:text-gray-400">
          {caption}
        </figcaption>
      </a>
    </figure>
  )
}

/** -----------------------------
 *  Page Route
 * ----------------------------- */
export default function SupportRoute() {
  return (
    <main className="container mx-auto text-black dark:text-white">
      <h1 className="text-3xl px-10 mb-6 text-cyan-900 dark:text-cyan-200">
        Support
      </h1>

      <div className="flex flex-col px-10 mb-6">
        <p>
          Thank you so much for even seeing this page! If you enjoy my content
          and/or want to help me succeed in my dream of working for myself,
          please consider some of the following options.
        </p>
      </div>

      {/* Support Options */}
      <div className="flex flex-col md:flex-row flex-wrap gap-6 px-10 my-12 justify-center">
        <SupportOptionCard
          href="https://discord.com/channels/442558623298617354/shop"
          label="Discord Supporter"
          icon="/images/discord.png"
          stars="⭐⭐⭐⭐⭐"
          percentage="90%*"
          preferred={true}
        />
        <SupportOptionCard
          href="https://www.patreon.com/jaseowns"
          label="Join Patreon"
          icon="/images/patreon.png"
          stars="⭐⭐⭐⭐"
          percentage="88–95%*"
        />
        <SupportOptionCard
          href="https://www.paypal.com/donate/?hosted_button_id=F7AV6EH6BB3NQ"
          label="Donate with PayPal"
          icon="/images/paypal.png"
          stars="⭐⭐⭐"
          percentage="87–96%*"
        />
        <SupportOptionCard
          href="https://www.youtube.com/jaseowns/join"
          label="YouTube Member"
          icon="/images/youtube.png"
          stars="⭐⭐"
          percentage="70%*"
        />
        <SupportOptionCard
          href="https://twitch.tv/jaseowns/subscribe"
          label="Twitch Subscriber"
          icon="/images/twitch.png"
          stars="⭐"
          percentage="50%*"
        />
      </div>

      <div className="flex flex-col px-10 mb-6 text-sm text-gray-600 dark:text-gray-400">
        <p>
          *The number is what percentage I get to keep, but{' '}
          <a href="https://discord.gg/jaseowns" className="underline">
            Discord
          </a>{' '}
          is a preferred option so we can keep in touch.
        </p>
      </div>

      {/* Affiliate Gear */}
      <h1 className="text-3xl px-10 mb-6 text-cyan-900 dark:text-cyan-200">
        Affiliate Links & Equipment Jase Uses
      </h1>
      <div className="flex flex-wrap justify-center px-10 mb-6 gap-6">
        {affiliateLinks.map((affiliate, index) => (
          <AffiliateCard key={`affiliate_${index}`} {...affiliate} />
        ))}
      </div>

      {/* Other Affiliate */}
      <h1 className="text-3xl px-10 mb-6 text-cyan-900 dark:text-cyan-200">
        Other Affiliate Links
      </h1>
      <div className="flex flex-wrap justify-center px-10 mb-6 gap-6">
        <OtherAffiliateCard
          href="https://share.uppbeat.io/7mrohtidcacy"
          image="/images/uppbeat.png"
          caption="Free music for creators"
          description="On my streams and videos I use Uppbeat.io. Here is a referral link if you are interested in music and sounds for your videos. No copyright claims. Your favorite beatmakers. #ad"
        />
      </div>

		<SponsorshipsSection />

      {/* CTA */}
      <div className="flex flex-wrap justify-center gap-8 rounded-3xl py-4">
        {WatchCTA()}
      </div>
    </main>
  )
}
